import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Edit2, Trash2, Save, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useCategory } from '@/contexts/CategoryManagementContext';
import CompactHeader from '@/components/layout/CompactHeader';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { useToast } from '@/components/ui/use-toast';

const SettingsPage = () => {
  const navigate = useNavigate();
  const { categories, addCategory, updateCategory, deleteCategory } = useCategory();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  
  // Form State
  const [formData, setFormData] = useState({
    name: '',
    color: '#000000',
    daysThreshold: 0,
    description: ''
  });

  const handleEdit = (cat) => {
    setEditingCategory(cat);
    setFormData(cat);
    setIsModalOpen(true);
  };

  const handleAdd = () => {
    setEditingCategory(null);
    setFormData({
      name: '',
      color: '#EF4444',
      daysThreshold: 0,
      description: ''
    });
    setIsModalOpen(true);
  };

  const handleSave = () => {
    if (editingCategory) {
      updateCategory(editingCategory.id, formData);
    } else {
      addCategory(formData);
    }
    setIsModalOpen(false);
  };

  return (
    <>
      <Helmet>
        <title>Configurações - Dashboard Center</title>
      </Helmet>
      
      <div className="min-h-screen bg-[#0a0a0a]">
        <CompactHeader />
        
        <main className="p-4 md:p-8 max-w-5xl mx-auto">
          <div className="flex items-center gap-4 mb-8">
            <Button variant="ghost" className="text-gray-400 hover:text-white" onClick={() => navigate('/dashboard')}>
              <ArrowLeft className="w-5 h-5 mr-2" />
              Voltar
            </Button>
            <h1 className="text-3xl font-bold text-white">Configurações</h1>
          </div>

          <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
            <div className="flex justify-between items-center mb-6 border-b border-white/10 pb-4">
              <div>
                <h2 className="text-xl font-bold text-white">Categorias de Urgência</h2>
                <p className="text-gray-400 text-sm">Gerencie os prazos e cores para indicadores de urgência</p>
              </div>
              <Button onClick={handleAdd} className="bg-orange-500 hover:bg-orange-600">
                <Plus className="w-4 h-4 mr-2" /> Nova Categoria
              </Button>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="bg-white/5 text-gray-400 uppercase font-bold text-xs">
                  <tr>
                    <th className="p-4 rounded-tl-lg">Cor</th>
                    <th className="p-4">Nome</th>
                    <th className="p-4">Limite (Dias)</th>
                    <th className="p-4">Descrição</th>
                    <th className="p-4 rounded-tr-lg text-right">Ações</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/10">
                  {categories.map((cat) => (
                    <tr key={cat.id} className="hover:bg-white/5 transition-colors">
                      <td className="p-4">
                        <div 
                          className="w-8 h-8 rounded-full border-2 border-white/20 shadow-sm" 
                          style={{ backgroundColor: cat.color }}
                        />
                      </td>
                      <td className="p-4 font-bold text-white">{cat.name}</td>
                      <td className="p-4 text-gray-300">
                        Até <span className="font-mono font-bold text-white bg-white/10 px-2 py-0.5 rounded">{cat.daysThreshold}</span> dias
                      </td>
                      <td className="p-4 text-gray-400">{cat.description}</td>
                      <td className="p-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button size="sm" variant="ghost" onClick={() => handleEdit(cat)}>
                            <Edit2 className="w-4 h-4 text-gray-400 hover:text-white" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => deleteCategory(cat.id)}>
                            <Trash2 className="w-4 h-4 text-red-500 hover:text-red-400" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </main>
      </div>

      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="bg-gray-900 border-gray-800 text-white">
          <DialogHeader>
            <DialogTitle>{editingCategory ? 'Editar Categoria' : 'Nova Categoria'}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-400">Nome da Categoria</label>
              <input 
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                className="w-full bg-black/50 border border-gray-700 rounded-md p-2 text-white focus:border-orange-500 outline-none"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-400">Cor (Hex)</label>
                <div className="flex gap-2">
                  <input 
                    type="color"
                    value={formData.color}
                    onChange={(e) => setFormData({...formData, color: e.target.value})}
                    className="h-10 w-12 rounded cursor-pointer bg-transparent border-none"
                  />
                  <input 
                    value={formData.color}
                    onChange={(e) => setFormData({...formData, color: e.target.value})}
                    className="flex-1 bg-black/50 border border-gray-700 rounded-md p-2 text-white focus:border-orange-500 outline-none font-mono"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-400">Limite de Dias</label>
                <input 
                  type="number"
                  value={formData.daysThreshold}
                  onChange={(e) => setFormData({...formData, daysThreshold: parseInt(e.target.value)})}
                  className="w-full bg-black/50 border border-gray-700 rounded-md p-2 text-white focus:border-orange-500 outline-none"
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-400">Descrição</label>
              <input 
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                className="w-full bg-black/50 border border-gray-700 rounded-md p-2 text-white focus:border-orange-500 outline-none"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsModalOpen(false)} className="border-gray-700 text-gray-300 hover:bg-gray-800">Cancelar</Button>
            <Button onClick={handleSave} className="bg-orange-500 hover:bg-orange-600 text-white">Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default SettingsPage;